import { ParentSurface } from "./ParentSurface";
import { ALL_ACCESS_MINOR, naira } from "../../lib/format";

interface ParentPaidScreenProps {
  childName: string;
  /** When the access they just bought runs to. */
  validUntil?: string | undefined;
  /** True while the charge is still clearing — transfers confirm late. */
  pending?: boolean | undefined;
}

/**
 * What a parent sees after paying.
 *
 * They were being sent to the student's success screen, which says "Keep
 * practicing" — an invitation to sit an exam they are not sitting, in an app
 * they have no account for. A parent's involvement ends here, and the screen
 * should say so rather than trying to convert them into a user.
 *
 * So: thank them, confirm what was bought and for how long, and tell them the
 * one thing they might otherwise worry about — that there is nothing more for
 * them to do and nothing that will charge them again.
 */
export function ParentPaidScreen({
  childName,
  validUntil,
  pending = false,
}: ParentPaidScreenProps) {
  return (
    <ParentSurface>
      <div
        aria-hidden="true"
        className={`mt-8 grid h-14 w-14 place-items-center rounded-full text-[24px] ${
          pending ? "bg-surface-high text-text-muted" : "bg-[var(--positive-tint)] text-positive"
        }`}
      >
        {pending ? "⏱" : "✓"}
      </div>

      <h1 className="m-0 pt-5 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.25]">
        {pending ? "Thank you — we're confirming it" : "Thank you"}
      </h1>

      <p className="m-0 pt-3 text-[14px] leading-[1.6] text-text-muted">
        {pending ? (
          <>
            Your bank is still confirming the payment. It usually takes under a minute, and{" "}
            {childName}'s practice unlocks the moment it lands. You don't need to do anything.
          </>
        ) : (
          <>
            {childName}'s practice is unlocked. Everything they chose is open now
            {validUntil ? `, until ${validUntil}` : ""}.
          </>
        )}
      </p>

      <dl className="m-0 mt-6 flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-5">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="m-0 text-[13px] text-text-muted">Paid</dt>
          <dd className="tabular m-0 text-[14px] font-semibold">{naira(ALL_ACCESS_MINOR)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="m-0 text-[13px] text-text-muted">Covers</dt>
          <dd className="m-0 text-right text-[14px] font-semibold">
            Every subject {childName} chose
          </dd>
        </div>
        {validUntil ? (
          <div className="flex items-baseline justify-between gap-3">
            <dt className="m-0 text-[13px] text-text-muted">Until</dt>
            <dd className="tabular m-0 text-[14px] font-semibold">{validUntil}</dd>
          </div>
        ) : null}
      </dl>

      {/*
        The two things a parent actually worries about after paying a stranger
        on their phone: will this happen again, and do I now have to manage
        something.
      */}
      <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-6">
        {[
          "One payment. No subscription, and nothing renews.",
          "No account to create — you can close this page.",
          `We'll tell ${childName} it's unlocked.`,
        ].map((item) => (
          <li key={item} className="flex items-start gap-2.5 text-[13.5px] leading-[1.5]">
            <span aria-hidden="true" className="pt-0.5 text-positive">
              ✓
            </span>
            {item}
          </li>
        ))}
      </ul>

      <p className="mt-auto pt-8 text-[12.5px] leading-[1.55] text-text-muted">
        Keep this page or your bank alert as your receipt. Questions? Message us on WhatsApp and
        quote the payment.
      </p>
    </ParentSurface>
  );
}
